import type { WidgetConfig } from '../types/widget'

function panelStorageBase(config: WidgetConfig): string {
  const cid = (config.clientId ?? '').trim() || 'default'
  const tid = (config.tenantId ?? '').trim() || 'default'
  let origin = 'unknown'
  try {
    if (typeof window !== 'undefined') origin = window.location.origin
  } catch {
    /* ignore */
  }
  return `voicnav:chatPanel:${origin}:${cid}:${tid}`
}

function explicitlyClosedKey(config: WidgetConfig): string {
  return `${panelStorageBase(config)}:explicitlyClosed`
}

function everOpenedKey(config: WidgetConfig): string {
  return `${panelStorageBase(config)}:everOpened`
}

/** User chose to close the chat panel (X, Escape, etc.); survives refresh until they open again. */
export function readChatPanelExplicitlyClosed(config: WidgetConfig): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(explicitlyClosedKey(config)) === '1'
  } catch {
    return false
  }
}

export function writeChatPanelExplicitlyClosed(config: WidgetConfig, closed: boolean): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return
  try {
    const k = explicitlyClosedKey(config)
    if (closed) localStorage.setItem(k, '1')
    else localStorage.removeItem(k)
  } catch {
    /* ignore */
  }
}

/** Set once the user opens the panel; allows staying open on refresh when host used defaultPanelOpen: false initially. */
export function readChatPanelEverOpened(config: WidgetConfig): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(everOpenedKey(config)) === '1'
  } catch {
    return false
  }
}

export function writeChatPanelEverOpened(config: WidgetConfig): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(everOpenedKey(config), '1')
  } catch {
    /* ignore */
  }
}

/**
 * Initial panel visibility after load.
 * - Stays open across refreshes unless the user explicitly closed it.
 * - Host may set defaultPanelOpen: false for a closed first visit; after the user opens once, refresh keeps it open.
 */
export function getInitialChatPanelOpen(config: WidgetConfig): boolean {
  if (readChatPanelExplicitlyClosed(config)) return false
  if (readChatPanelEverOpened(config)) return true
  return config.defaultPanelOpen !== false
}
